export class Animations {
  private container: HTMLElement;
  private particleContainer: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
    this.particleContainer = document.createElement('div');
    this.particleContainer.className = 'dd-particles';
    this.particleContainer.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;overflow:hidden;';
    document.body.appendChild(this.particleContainer);
  }

  /**
   * Spawn floating text (e.g., "+$500" or "+5 Rep") that drifts upward and fades out.
   */
  floatingText(x: number, y: number, text: string, color: string): void {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = [
      'position:fixed',
      'pointer-events:none',
      'font-weight:bold',
      'font-size:18px',
      'white-space:nowrap',
      `color:${color}`,
      `left:${x}px`,
      `top:${y}px`,
      'opacity:1',
      'transform:translateY(0)',
      'transition:transform 1.5s ease-out, opacity 1.5s ease-out',
      'z-index:10000',
      'text-shadow:0 1px 3px rgba(0,0,0,0.5)',
    ].join(';');

    this.particleContainer.appendChild(el);

    // Force reflow so the initial values are applied before transitioning
    el.getBoundingClientRect();

    el.style.transform = 'translateY(-60px)';
    el.style.opacity = '0';

    el.addEventListener('transitionend', () => {
      el.remove();
    }, { once: true });
  }

  /**
   * Spawn count small circular particles at (x, y) with random velocities,
   * animated via requestAnimationFrame with gravity and fade-out over 800ms.
   */
  spawnParticles(x: number, y: number, count: number, color: string): void {
    const duration = 800;

    interface Particle {
      el: HTMLElement;
      vx: number;
      vy: number;
    }

    const particles: Particle[] = [];

    for (let i = 0; i < count; i++) {
      const el = document.createElement('div');
      el.style.cssText = [
        'position:fixed',
        'pointer-events:none',
        'width:6px',
        'height:6px',
        'border-radius:50%',
        `background:${color}`,
        `left:${x - 3}px`,
        `top:${y - 3}px`,
        'opacity:1',
      ].join(';');
      this.particleContainer.appendChild(el);

      const angle = Math.random() * Math.PI * 2;
      const speed = 50 + Math.random() * 120;
      particles.push({
        el,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
      });
    }

    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      for (const p of particles) {
        const dt = elapsed / 1000;
        const px = x + p.vx * dt;
        const py = y + p.vy * dt + 0.5 * 400 * dt * dt; // gravity = 400 px/s^2
        p.el.style.left = `${px - 3}px`;
        p.el.style.top = `${py - 3}px`;
        p.el.style.opacity = `${1 - progress}`;
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        for (const p of particles) {
          p.el.remove();
        }
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Screen shake effect: oscillate the container with random translate offsets.
   */
  screenShake(intensity: number, duration: number): void {
    const startTime = performance.now();
    const originalTransform = this.container.style.transform;

    const shake = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      if (progress < 1) {
        const decay = 1 - progress;
        const offsetX = (Math.random() * 2 - 1) * intensity * decay;
        const offsetY = (Math.random() * 2 - 1) * intensity * decay;
        this.container.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        requestAnimationFrame(shake);
      } else {
        this.container.style.transform = originalTransform || 'none';
      }
    };

    requestAnimationFrame(shake);
  }

  /**
   * Flash the screen edges with a full-screen color overlay that fades out.
   */
  screenFlash(color: string, duration: number): void {
    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'pointer-events:none',
      'z-index:9998',
      `background:${color}`,
      'opacity:0.3',
      `transition:opacity ${duration}ms ease-out`,
    ].join(';');

    document.body.appendChild(overlay);

    // Force reflow
    overlay.getBoundingClientRect();

    overlay.style.opacity = '0';

    overlay.addEventListener('transitionend', () => {
      overlay.remove();
    }, { once: true });
  }

  /**
   * Pulse an element: scale to 1.1 and back twice to draw attention.
   */
  pulseElement(element: HTMLElement): void {
    const originalTransform = element.style.transform;
    const originalTransition = element.style.transition;

    element.style.transition = 'transform 150ms ease-in-out';

    let step = 0;
    const totalSteps = 4; // up, down, up, down

    const nextStep = () => {
      step++;
      if (step > totalSteps) {
        element.style.transform = originalTransform || '';
        element.style.transition = originalTransition || '';
        return;
      }

      if (step % 2 === 1) {
        element.style.transform = 'scale(1.1)';
      } else {
        element.style.transform = 'scale(1)';
      }

      element.addEventListener('transitionend', nextStep, { once: true });
    };

    // Kick off first pulse
    element.style.transform = 'scale(1.1)';
    element.addEventListener('transitionend', nextStep, { once: true });
  }

  /**
   * Slide an element in from the right side of the screen.
   */
  slideInRight(element: HTMLElement, duration: number = 300): void {
    element.style.transform = 'translateX(100%)';
    element.style.transition = `transform ${duration}ms ease-out`;

    // Force reflow
    element.getBoundingClientRect();

    element.style.transform = 'translateX(0)';
  }

  /**
   * Fade an element in from opacity 0.
   */
  fadeIn(element: HTMLElement, duration: number = 300): void {
    element.style.opacity = '0';
    element.style.transition = `opacity ${duration}ms ease-in`;

    // Force reflow
    element.getBoundingClientRect();

    element.style.opacity = '1';
  }

  /**
   * Fade an element out and remove it from the DOM after the transition.
   */
  fadeOutAndRemove(element: HTMLElement, duration: number = 300): void {
    element.style.transition = `opacity ${duration}ms ease-out`;

    // Force reflow
    element.getBoundingClientRect();

    element.style.opacity = '0';

    element.addEventListener('transitionend', () => {
      element.remove();
    }, { once: true });
  }

  /**
   * Gold shimmer effect for rare finds: 12 golden particles that spiral outward.
   */
  goldShimmer(x: number, y: number): void {
    const particleCount = 12;
    const duration = 1200;
    const colors = ['#D4A03C', '#B8860B'];

    interface ShimmerParticle {
      el: HTMLElement;
      angle: number;
      speed: number;
      rotationSpeed: number;
    }

    const particles: ShimmerParticle[] = [];

    for (let i = 0; i < particleCount; i++) {
      const el = document.createElement('div');
      const color = colors[i % colors.length];
      const angle = (i / particleCount) * Math.PI * 2;

      el.style.cssText = [
        'position:fixed',
        'pointer-events:none',
        'width:8px',
        'height:8px',
        `background:${color}`,
        `left:${x - 4}px`,
        `top:${y - 4}px`,
        'opacity:1',
        'transform:rotate(45deg)',
        `box-shadow:0 0 4px ${color}`,
      ].join(';');

      this.particleContainer.appendChild(el);

      particles.push({
        el,
        angle,
        speed: 40 + Math.random() * 60,
        rotationSpeed: 2 + Math.random() * 4,
      });
    }

    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const dt = elapsed / 1000;

      for (const p of particles) {
        // Spiral outward: radius grows over time, angle advances
        const radius = p.speed * dt;
        const currentAngle = p.angle + p.rotationSpeed * dt;
        const px = x + Math.cos(currentAngle) * radius;
        const py = y + Math.sin(currentAngle) * radius;
        const rotation = 45 + (elapsed / 1000) * 360;

        p.el.style.left = `${px - 4}px`;
        p.el.style.top = `${py - 4}px`;
        p.el.style.opacity = `${1 - progress}`;
        p.el.style.transform = `rotate(${rotation}deg) scale(${1 - progress * 0.5})`;
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        for (const p of particles) {
          p.el.remove();
        }
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Clean up the particle container and any lingering animation elements.
   */
  destroy(): void {
    this.particleContainer.remove();
  }
}
